import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import cognitoService from '../lib/cognito';
import { login as authLogin, logout as authLogout } from '../lib/auth';

// GraphQL query to get auth context with memberships
const GET_MY_AUTH_CONTEXT = gql`
  query GetMyAuthContext {
    getMyAuthContext {
      userId
      email
      legacyRole
      highestRole
      isAdmin
      memberships {
        id
        role
        isActive
        organization {
          id
          name
        }
        parkingIds
        parkings {
          id
          name
          code
        }
      }
      legacyOrganization {
        id
        name
      }
    }
  }
`;

export type MembershipRole = 'ADMIN' | 'OWNER' | 'STAFF';

export interface UserMembership {
  id: string;
  role: MembershipRole;
  isActive: boolean;
  organization: {
    id: string;
    name: string;
  } | null;
  parkingIds: string[] | null;
  parkings: Array<{
    id: string;
    name: string;
    code: string;
  }> | null;
}

export interface LegacyOrganization {
  id: string;
  name: string;
}

export interface AuthContextData {
  userId: string;
  email: string;
  legacyRole: string;
  highestRole: MembershipRole | null;
  isAdmin: boolean;
  memberships: UserMembership[];
  legacyOrganization: LegacyOrganization | null;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthContextData | null;
  memberships: UserMembership[];
  legacyOrganization: LegacyOrganization | null;
  error: Error | null;

  // Role helpers
  isAdmin: () => boolean;
  isOwnerOf: (orgId: string) => boolean;
  isStaffAt: (parkingId: string) => boolean;
  hasAnyRole: (...roles: MembershipRole[]) => boolean;
  getHighestRole: () => MembershipRole | null;

  // Access helpers
  canAccessOrg: (orgId: string) => boolean;
  canAccessParking: (parkingId: string) => boolean;
  canManageOrg: (orgId: string) => boolean;

  // Scope getters
  getAccessibleOrgIds: () => string[];
  getAccessibleParkingIds: () => string[];

  // Actions
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  logout: () => Promise<void>;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = await cognitoService.isAuthenticated();
        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  // Query auth context when authenticated
  const { data, loading: queryLoading, error, refetch } = useQuery(GET_MY_AUTH_CONTEXT, {
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  });

  const user = data?.getMyAuthContext || null;
  const memberships = user?.memberships || [];
  const legacyOrganization = user?.legacyOrganization || null;

  const isLoading = isCheckingAuth || (isAuthenticated && queryLoading);

  // Role check helpers
  const isAdmin = useCallback(() => {
    // Check memberships first
    if (memberships.some((m: UserMembership) => m.role === 'ADMIN' && m.isActive)) {
      return true;
    }
    // Fallback to legacy role for users without memberships yet
    if (user?.legacyRole === 'ADMINISTRATOR') {
      return true;
    }
    // Also check the isAdmin field from the query
    return user?.isAdmin === true;
  }, [memberships, user]);

  const isOwnerOf = useCallback((orgId: string) => {
    return memberships.some((m: UserMembership) =>
      m.role === 'OWNER' &&
      m.organization?.id === orgId &&
      m.isActive
    );
  }, [memberships]);

  const isStaffAt = useCallback((parkingId: string) => {
    return memberships.some((m: UserMembership) => {
      if (!m.isActive || m.role !== 'STAFF') return false;
      if (!m.parkingIds || m.parkingIds.length === 0) return true;
      return m.parkingIds.includes(parkingId);
    });
  }, [memberships]);

  const hasAnyRole = useCallback((...roles: MembershipRole[]) => {
    // Check memberships first
    if (memberships.some((m: UserMembership) => m.isActive && roles.includes(m.role))) {
      return true;
    }
    // Fallback to legacy role for users without memberships yet
    const legacyRoleMap: Record<string, MembershipRole> = {
      'ADMINISTRATOR': 'ADMIN',
      'OWNER': 'OWNER',
      'STAFF': 'STAFF',
    };
    const mappedRole = legacyRoleMap[user?.legacyRole || ''];
    return mappedRole ? roles.includes(mappedRole) : false;
  }, [memberships, user]);

  const getHighestRole = useCallback((): MembershipRole | null => {
    if (isAdmin()) return 'ADMIN';
    if (memberships.some((m: UserMembership) => m.role === 'OWNER' && m.isActive)) return 'OWNER';
    if (memberships.some((m: UserMembership) => m.role === 'STAFF' && m.isActive)) return 'STAFF';
    // Fallback to legacy role
    const legacyRoleMap: Record<string, MembershipRole> = {
      'ADMINISTRATOR': 'ADMIN',
      'OWNER': 'OWNER',
      'STAFF': 'STAFF',
    };
    return legacyRoleMap[user?.legacyRole || ''] || null;
  }, [memberships, isAdmin, user]);

  // Access check helpers
  const canAccessOrg = useCallback((orgId: string) => {
    if (isAdmin()) return true;
    return memberships.some((m: UserMembership) =>
      m.organization?.id === orgId && m.isActive
    );
  }, [memberships, isAdmin]);

  const canAccessParking = useCallback((parkingId: string) => {
    if (isAdmin()) return true;
    return memberships.some((m: UserMembership) => {
      if (!m.isActive) return false;
      if (!m.parkingIds || m.parkingIds.length === 0) return true;
      return m.parkingIds.includes(parkingId);
    });
  }, [memberships, isAdmin]);

  const canManageOrg = useCallback((orgId: string) => {
    if (isAdmin()) return true;
    return isOwnerOf(orgId);
  }, [isAdmin, isOwnerOf]);

  // Scope getters
  const getAccessibleOrgIds = useCallback(() => {
    if (isAdmin()) return []; // Empty means all
    return memberships
      .filter((m: UserMembership) => m.isActive && m.organization?.id)
      .map((m: UserMembership) => m.organization!.id);
  }, [memberships, isAdmin]);

  const getAccessibleParkingIds = useCallback(() => {
    if (isAdmin()) return []; // Empty means all
    const parkingIds: string[] = [];
    memberships.forEach((m: UserMembership) => {
      if (m.isActive && m.parkingIds && m.parkingIds.length > 0) {
        parkingIds.push(...m.parkingIds);
      }
    });
    return [...new Set(parkingIds)];
  }, [memberships, isAdmin]);

  // Actions
  const login = useCallback(async (username: string, password: string) => {
    const result = await authLogin(username, password);
    if (result.success) {
      setIsAuthenticated(true);
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    await authLogout();
    setIsAuthenticated(false);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    isAuthenticated,
    isLoading,
    user,
    memberships,
    legacyOrganization,
    error: error || null,

    isAdmin,
    isOwnerOf,
    isStaffAt,
    hasAnyRole,
    getHighestRole,

    canAccessOrg,
    canAccessParking,
    canManageOrg,

    getAccessibleOrgIds,
    getAccessibleParkingIds,

    login,
    logout,
    refetch,
  }), [
    isAuthenticated,
    isLoading,
    user,
    memberships,
    legacyOrganization,
    error,
    isAdmin,
    isOwnerOf,
    isStaffAt,
    hasAnyRole,
    getHighestRole,
    canAccessOrg,
    canAccessParking,
    canManageOrg,
    getAccessibleOrgIds,
    getAccessibleParkingIds,
    login,
    logout,
    refetch,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
